import { useCallback, useEffect, useState } from "react";

import { SupplierService } from "../api/supplier.service";
import {
  Supplier,
  SupplierFilters,
  SupplierPayload,
  RecordStatus,
  Pagination,
} from "../types";

const DEFAULT_FILTERS: SupplierFilters = {
  page: 1,
  limit: 10,
  search: "",
  status: undefined,
  sortBy: "createdAt",
  sortOrder: "desc",
};

const DEFAULT_PAGINATION: Pagination = {
  page: 1,
  limit: 10,
  total: 0,
  totalPages: 0,
};

export const useSuppliers = () => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] =
    useState<SupplierFilters>(DEFAULT_FILTERS);

  const [pagination, setPagination] =
    useState<Pagination>(DEFAULT_PAGINATION);

  const fetchSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response =
        await SupplierService.getSuppliers(filters);

      setSuppliers(response.data);
      setPagination(response.pagination);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch suppliers"
      );
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const createSupplier = async (
    payload: SupplierPayload
  ) => {
    await SupplierService.createSupplier(payload);

    await fetchSuppliers();
  };

  const updateSupplier = async (
    id: string,
    payload: SupplierPayload
  ) => {
    await SupplierService.updateSupplier(
      id,
      payload
    );

    await fetchSuppliers();
  };

  const deleteSupplier = async (
    id: string
  ) => {
    await SupplierService.deleteSupplier(id);

    await fetchSuppliers();
  };

  const updateStatus = async (
    id: string,
    status: RecordStatus
  ) => {
    await SupplierService.updateStatus(
      id,
      status
    );

    await fetchSuppliers();
  };

  const updateFilters = (
    values: Partial<SupplierFilters>
  ) => {
    setFilters((prev) => ({
      ...prev,
      ...values,
    }));
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
  };

  return {
    suppliers,

    loading,

    error,

    filters,

    pagination,

    fetchSuppliers,

    createSupplier,

    updateSupplier,

    deleteSupplier,

    updateStatus,

    updateFilters,

    resetFilters,
  };
};
